import { useState } from 'react';
import { LockKeyhole } from 'lucide-react';

interface DemoCardWidgetProps {
  committeeName: string;
  amount: number;
  /** Fires the demo simulate write. Returns once the gift has landed (or failed). */
  onDonate: () => void | Promise<void>;
  submitting: boolean;
  /** Set when the simulate call failed, so the widget can surface a soft error. */
  errored?: boolean;
}

/**
 * The demo stand-in for Stripe's Embedded Checkout card widget (issue #319). On a demo campaign the
 * real branded page renders as-is, but this fake card widget replaces the final card-entry step —
 * per the demo's drift tolerance it need NOT track Stripe's Embedded Checkout UI. Its Donate button
 * calls the demo simulate endpoint instead of charging a card; the inputs are inert placeholders so
 * a rep can show a realistic "enter your card" moment without any Stripe key or real charge.
 */
export function DemoCardWidget({
  committeeName,
  amount,
  onDonate,
  submitting,
  errored,
}: DemoCardWidgetProps) {
  // Local-only, never read: the card fields exist for the visual beat, not for a charge.
  const [cardNumber, setCardNumber] = useState('4242 4242 4242 4242');
  const [expiry, setExpiry] = useState('12 / 30');
  const [cvc, setCvc] = useState('123');

  return (
    <section className="donation-panel space-y-5" aria-labelledby="demo-card-heading">
      <div>
        <h2 id="demo-card-heading" className="donation-section-title">
          Payment
        </h2>
        <p className="donation-section-description">
          Demo mode — no real card is charged. Clicking Donate records a simulated gift for{' '}
          {committeeName}.
        </p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="demo-card-number" className="donation-label">
          Card number
        </label>
        <input
          id="demo-card-number"
          className="donation-control"
          value={cardNumber}
          onChange={(e) => setCardNumber(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label htmlFor="demo-card-expiry" className="donation-label">
            Expiry
          </label>
          <input
            id="demo-card-expiry"
            className="donation-control"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="demo-card-cvc" className="donation-label">
            CVC
          </label>
          <input
            id="demo-card-cvc"
            className="donation-control"
            value={cvc}
            onChange={(e) => setCvc(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>

      {errored && (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
          That didn&apos;t go through. Please try again.
        </p>
      )}

      <div className="donation-sticky-action">
        <div className="donation-shell">
          <button
            type="button"
            onClick={() => onDonate()}
            disabled={submitting}
            className="donation-submit"
          >
            {submitting ? 'Please wait…' : `Donate $${amount.toFixed(2)}`}
          </button>
          <p>
            <LockKeyhole aria-hidden="true" /> Demo simulation — your card is never charged.
          </p>
        </div>
      </div>
    </section>
  );
}
